import { useEffect, useState } from 'react'
import { AlertCircle, CheckCircle2, FileSignature, Lock, Shield, XCircle } from 'lucide-react'
import api from '@/services/api'
import { apiErrorText } from '@/utils/apiErrorText'
import { useLocalAuth } from './LocalAuthContext'

type AgreementSection = {
  heading: string
  body: string[]
}

type DocumentInfo = {
  required: boolean
  accepted_at: string | null
  title: string
  version: string
  sections: AgreementSection[]
}

type AgreementPayload = {
  required: boolean
  agreement: DocumentInfo
  nda: DocumentInfo | null
  // Backward-compat flat fields
  accepted_at: string | null
  title: string
  version: string
  sections: AgreementSection[]
}

export default function TrialAgreementGate() {
  const { localUser, acceptTrialAgreement, logout } = useLocalAuth()
  const [payload, setPayload] = useState<AgreementPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmConfidentiality, setConfirmConfidentiality] = useState(false)
  const [confirmAuthority, setConfirmAuthority] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await api.get<AgreementPayload>('/auth/trial-agreement')
        if (!cancelled) {
          setPayload(res.data)
        }
      } catch {
        if (!cancelled) {
          setError('Unable to load the trial agreement. Check API connectivity and try again.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handleAccept = async () => {
    setAccepting(true)
    setError(null)
    try {
      await acceptTrialAgreement()
    } catch (err: unknown) {
      setError(apiErrorText(err, 'Agreement acceptance failed. Try again.'))
    } finally {
      setAccepting(false)
    }
  }

  // Gather all document sections to display
  const agreementDoc = payload?.agreement
  const ndaDoc = payload?.nda
  const showAgreement = agreementDoc?.required
  const showNda = ndaDoc?.required

  // Build the title based on which documents are required
  const pageTitle = showAgreement && showNda
    ? 'Trial Agreement & NDA'
    : showNda
      ? 'Non-Disclosure Agreement'
      : (agreementDoc?.title || 'Trial License Agreement')

  return (
    <div className="min-h-screen w-full bg-[radial-gradient(circle_at_top,rgba(217,183,104,0.16),transparent_24%),linear-gradient(180deg,#050505_0%,#080808_100%)] px-4 py-8 text-stone-100 sm:px-6 lg:px-10">
      <div className="mx-auto max-w-5xl">
        <div className="login-dialog overflow-hidden">
          <div className="border-b border-amber-400/10 px-6 py-6 sm:px-8">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="text-[11px] font-medium uppercase tracking-[0.34em] text-[#d9b768]">Trial Access Agreement</div>
                <h1 className="mt-3 text-3xl font-medium text-white">{pageTitle}</h1>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-stone-400">
                  Trial access is gated until the named license holder electronically accepts the confidentiality, usage, monitoring, and restriction terms below.
                </p>
              </div>

              <div className="grid gap-3 sm:min-w-[320px]">
                <div className="rounded-2xl border border-amber-400/10 bg-black/25 px-4 py-3">
                  <div className="text-[10px] font-medium uppercase tracking-[0.24em] text-stone-500">License Holder</div>
                  <div className="mt-1 text-sm font-medium text-stone-100">{localUser?.name}</div>
                  <div className="text-[12px] text-stone-400">{localUser?.email}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-amber-400/10 bg-black/25 px-4 py-3">
                    <div className="text-[10px] font-medium uppercase tracking-[0.24em] text-stone-500">Agreement Version</div>
                    <div className="mt-1 text-sm font-medium text-stone-100">{agreementDoc?.version || 'Pending'}</div>
                  </div>
                  <div className="rounded-2xl border border-amber-400/10 bg-black/25 px-4 py-3">
                    <div className="text-[10px] font-medium uppercase tracking-[0.24em] text-stone-500">Trial Expires</div>
                    <div className="mt-1 text-sm font-medium text-stone-100">
                      {localUser?.trial_expires_at ? new Date(localUser.trial_expires_at).toLocaleString() : 'Not set'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-0 lg:grid-cols-[1.4fr_0.8fr]">
            <div className="max-h-[calc(100vh-14rem)] overflow-y-auto border-b border-amber-400/10 px-6 py-6 sm:px-8 lg:border-b-0 lg:border-r">
              {loading ? (
                <div className="text-sm text-stone-500">Loading agreement…</div>
              ) : null}

              {error ? (
                <div className="mb-5 flex items-start gap-2 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              ) : null}

              {/* Trial Agreement sections */}
              {showAgreement && agreementDoc ? (
                <div className="space-y-6">
                  <div className="text-[11px] font-medium uppercase tracking-[0.28em] text-[#d9b768]">{agreementDoc.title}</div>
                  {agreementDoc.sections.map((section) => (
                    <section key={section.heading} className="rounded-[24px] border border-amber-400/10 bg-black/20 px-5 py-4">
                      <div className="flex items-center gap-2 text-[#f6df9a]">
                        <FileSignature size={14} />
                        <h2 className="text-[13px] font-semibold uppercase tracking-[0.2em]">{section.heading}</h2>
                      </div>
                      <div className="mt-3 space-y-2">
                        {section.body.map((paragraph) => (
                          <p key={paragraph} className="text-sm leading-7 text-stone-300">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : null}

              {/* NDA sections */}
              {showNda && ndaDoc ? (
                <div className={`space-y-6 ${showAgreement ? 'mt-8 border-t border-amber-400/10 pt-8' : ''}`}>
                  <div className="text-[11px] font-medium uppercase tracking-[0.28em] text-[#d9b768]">{ndaDoc.title}</div>
                  {ndaDoc.sections.map((section) => (
                    <section key={section.heading} className="rounded-[24px] border border-amber-400/10 bg-black/20 px-5 py-4">
                      <div className="flex items-center gap-2 text-[#f6df9a]">
                        <FileSignature size={14} />
                        <h2 className="text-[13px] font-semibold uppercase tracking-[0.2em]">{section.heading}</h2>
                      </div>
                      <div className="mt-3 space-y-2">
                        {section.body.map((paragraph) => (
                          <p key={paragraph} className="text-sm leading-7 text-stone-300">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : null}

              {/* Fallback: show flat sections if new format isn't available */}
              {!showAgreement && !showNda && payload?.sections ? (
                <div className="space-y-6">
                  {payload.sections.map((section) => (
                    <section key={section.heading} className="rounded-[24px] border border-amber-400/10 bg-black/20 px-5 py-4">
                      <div className="flex items-center gap-2 text-[#f6df9a]">
                        <FileSignature size={14} />
                        <h2 className="text-[13px] font-semibold uppercase tracking-[0.2em]">{section.heading}</h2>
                      </div>
                      <div className="mt-3 space-y-2">
                        {section.body.map((paragraph) => (
                          <p key={paragraph} className="text-sm leading-7 text-stone-300">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="px-6 py-6 sm:px-8">
              <div className="login-panel">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-amber-300/20 bg-amber-300/10 text-amber-100">
                    <Shield size={22} />
                  </div>
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#d9b768]">Execution Controls</div>
                    <div className="mt-1 text-lg font-medium text-white">Accept to Continue</div>
                  </div>
                </div>

                <div className="mt-5 space-y-3 text-sm text-stone-300">
                  <label className="flex items-start gap-3 rounded-2xl border border-amber-400/10 bg-black/25 px-4 py-3">
                    <input
                      checked={confirmConfidentiality}
                      className="mt-1 h-4 w-4 rounded border-amber-300/30 bg-black/60 accent-amber-400"
                      onChange={(event) => setConfirmConfidentiality(event.target.checked)}
                      type="checkbox"
                    />
                    <span>I understand the environment is confidential, monitored, and restricted to approved trial use only.</span>
                  </label>
                  <label className="flex items-start gap-3 rounded-2xl border border-amber-400/10 bg-black/25 px-4 py-3">
                    <input
                      checked={confirmAuthority}
                      className="mt-1 h-4 w-4 rounded border-amber-300/30 bg-black/60 accent-amber-400"
                      onChange={(event) => setConfirmAuthority(event.target.checked)}
                      type="checkbox"
                    />
                    <span>
                      I have authority to electronically accept
                      {showAgreement && showNda ? ' the Trial Agreement and Non-Disclosure Agreement' : showNda ? ' the Non-Disclosure Agreement' : ' these terms'}
                      {' '}on behalf of the trial participant and named user.
                    </span>
                  </label>
                </div>

                <div className="mt-5 rounded-2xl border border-amber-400/10 bg-black/25 px-4 py-4 text-[12px] text-stone-400">
                  <div className="flex items-center gap-2 text-stone-200">
                    <Lock size={14} className="text-[#d9b768]" />
                    Electronic execution record
                  </div>
                  <div className="mt-2 leading-6">
                    Acceptance records the signer, timestamp, IP address, forwarded IP chain, and browser agent for audit and licensing enforcement.
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-3">
                  <button
                    className="login-action"
                    disabled={accepting || !confirmAuthority || !confirmConfidentiality || loading}
                    onClick={() => void handleAccept()}
                    type="button"
                  >
                    {accepting ? (
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-black/30 border-t-black" />
                    ) : (
                      <>
                        <CheckCircle2 size={16} />
                        Accept and Continue
                      </>
                    )}
                  </button>
                  <button
                    className="btn-secondary flex items-center justify-center gap-2 text-[12px]"
                    onClick={logout}
                    type="button"
                  >
                    <XCircle size={14} />
                    Decline and Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
